import { ReadonlyArray, NonEmptyArray, Option } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import * as Data from "~/Data";
import * as ErrorMessage from "~/ErrorMessage";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";
import * as Table from "~/Table";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export const Component = (route: Log.Route) => (
  <Table.Component>
    <Table.Head.Component>
      <Table.Row.Component>
        <Table.Header.Component>Type</Table.Header.Component>
        <Table.Header.Component>Time</Table.Header.Component>
        <Table.Header.Component>Description</Table.Header.Component>
      </Table.Row.Component>
    </Table.Head.Component>
    {pipe(Log.Issue.Count.useFromActiveLog(route), ({ danger, warning }) =>
      danger + warning === 0 ? (
        <Table.Wide.Empty.Component colSpan={3} />
      ) : (
        <>
          <Violations.Component {...route} />
          <NotSigned.Component {...route} />
        </>
      )
    )}
  </Table.Component>
);

export namespace Violations {
  export const Component = (route: Log.Route) =>
    Data.Remote.when(Log.Violation.use(route), {
      loading: () => <Table.Wide.Loading.Component colSpan={3} />,
      error: (error) => (
        <Table.Wide.ErrorMessage.Component colSpan={3}>
          {error.message}
        </Table.Wide.ErrorMessage.Component>
      ),
      success: flow(
        ReadonlyArray.mapWithIndex((index, violation) => (
          <Violation.Component key={index} {...violation} />
        )),
        NonEmptyArray.fromReadonlyArray,
        Option.map((rows) => (
          <Table.Body.Component>{rows}</Table.Body.Component>
        )),
        Option.toNullable
      ),
    });
}

namespace Violation {
  export const Component: React.Fn<Log.Violation.Violation> = (violation) => (
    <Row.Component
      Time={() => <Time.Interval {...violation} />}
      Description={() => <Log.Violation.Description.Component {...violation} />}
      Badge={() => (
        <Badge.Component danger>
          <Log.Violation.Container.Type.Component {...violation} />
        </Badge.Component>
      )}
    />
  );
}

namespace NotSigned {
  export const Component = (route: Log.Route) =>
    Log.Signature.useFromActiveLog(route, {
      found: () => null,
      missing: () => (
        <Table.Body.Component>
          <Row.Component
            Badge={() => <Badge.Component warning>Not Signed</Badge.Component>}
            Description={() => <>This log hasn't been signed.</>}
          />
        </Table.Body.Component>
      ),
    });
}

namespace Row {
  export const Component = ({
    Badge,
    Time = () => <NotApplicable />,
    Description,
  }: {
    readonly Badge: Component;
    readonly Time?: Component;
    readonly Description: Component;
  }) => (
    <Table.Row.Component>
      <Table.Column.Component style={{ width: "10em" }}>
        <Badge />
      </Table.Column.Component>
      <Table.Column.Component>
        <Time />
      </Table.Column.Component>
      <Table.Column.Component>
        <Description />
      </Table.Column.Component>
    </Table.Row.Component>
  );
}

export namespace Badge {
  export const Component: React.Fn<ErrorMessage.ErrorMessage> = ({
    children,
    ...props
  }) => (
    <Container.Component {...props}>
      <Typography.Component strong>{children}</Typography.Component>
    </Container.Component>
  );

  namespace Container {
    export const Component = styled.div<ErrorMessage.ErrorMessage>`
      padding: ${Layout.Inline.padding()};

      border-radius: 0.5em;
      background: ${({ danger, warning }) =>
        danger
          ? ErrorMessage.Danger.color()
          : warning
          ? ErrorMessage.Warning.color()
          : Color.steel()};

      text-align: center;
      color: ${({ warning }) => (warning ? Color.black() : Color.white())};
    `;
  }
}
